import { useCallback, useRef } from 'react';

/**
 * Wraps an API function with automatic and manual request cancellation.
 * Only one outstanding API call is allowed; previous requests are cancelled as new ones are made.
 *
 * @param {function} apiFn - The API function to wrap.  This function should accept a cancellationController as its first argument
 * @param {boolean} autoCancel - If true, subsequent api calls will cancel any outstanding request
 * @returns {[function, function]} - An array containing the curried API function and a cancel function.
 */
export const useApiWithCancel = (apiFn, autoCancel = true) => {
  const cancelFnRef = useRef(undefined);

  const apiCall = useCallback(
    (...args) => {
      if (autoCancel && cancelFnRef.current) {
        cancelFnRef.current();
        cancelFnRef.current = undefined;
      }

      const controller = new AbortController();
      cancelFnRef.current = () => controller.abort('stale');
      const { signal } = controller;
      return apiFn({ signal }, ...args)
        .then(res => {
          cancelFnRef.current = undefined;
          return res;
        })
        .catch(e => {
          // e is undefined on cancellation, safe to ignore
          if (e !== undefined) throw e;
        });
    },
    [apiFn]
  );

  const apiCancel = useCallback(() => {
    if (cancelFnRef.current) {
      cancelFnRef.current();
      cancelFnRef.current = undefined;
    }
  });

  return [apiCall, apiCancel];
};
